import { layoutRect, layoutCoordinate } from "../utils/displayDensity";
/* eslint-disable */
import { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { SkeletonMarketViewBubbleChart } from "./skeletons/SkeletonMarketViewBubbleChart";
import DiscordBubbleTooltip from "./DiscordBubbleTooltip";
import {
  BUBBLE_LABEL_FONT,
  makeMemberColorScale,
  toBubbleDatum,
} from "./discordBubbleUtils";
import { formatCompactNumber } from "../pages/discordApi";

// Largest tier first so the biggest communities read left-to-right.
export const MEMBER_TIERS = [
  { label: "1M+", min: 1_000_000, max: Infinity },
  { label: "500K–999K", min: 500_000, max: 1_000_000 },
  { label: "250K–499K", min: 250_000, max: 500_000 },
  { label: "100K–249K", min: 100_000, max: 250_000 },
  { label: "50K–99K", min: 50_000, max: 100_000 },
  { label: "25K–49K", min: 25_000, max: 50_000 },
  { label: "10K–24K", min: 10_000, max: 25_000 },
  { label: "<10K", min: 0, max: 10_000 },
];

const AXIS_H = 46;
const TOP_PAD = 12;

function tierIndex(members) {
  for (let i = 0; i < MEMBER_TIERS.length; i++) {
    const t = MEMBER_TIERS[i];
    if (members >= t.min && members < t.max) return i;
  }
  return MEMBER_TIERS.length - 1;
}

export default function DiscordTierView({ servers, loading }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });
  const [zoomT, setZoomT] = useState(d3.zoomIdentity);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const rect = layoutRect(el);
      setDims({ w: Math.max(300, rect.width), h: Math.max(300, rect.height) });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const colorScale = useMemo(() => makeMemberColorScale(servers), [servers]);

  const { nodes, columns } = useMemo(() => {
    const { w, h } = dims;
    if (!servers?.length || w <= 0 || h <= 0) return { nodes: [], columns: [] };

    const data = servers
      .map((s) => toBubbleDatum(s, colorScale))
      .filter((d) => d.value > 0);
    if (!data.length) return { nodes: [], columns: [] };

    // Only render tiers that actually contain servers, so empty columns don't
    // eat horizontal space.
    const used = MEMBER_TIERS.map((tier, i) => ({
      ...tier,
      index: i,
      count: data.filter((d) => tierIndex(d.members) === i).length,
    })).filter((t) => t.count > 0);
    if (!used.length) return { nodes: [], columns: [] };

    const colW = w / used.length;
    const colCenter = new Map(used.map((t, i) => [t.index, colW * (i + 0.5)]));
    const colLeft = new Map(used.map((t, i) => [t.index, colW * i]));

    const extent = d3.extent(data, (d) => d.members);
    const rScale = d3
      .scaleSqrt()
      .domain([Math.max(1, extent[0]), Math.max(2, extent[1])])
      .range([2.4, Math.min(26, colW * 0.16)])
      .clamp(true);

    const plotTop = TOP_PAD;
    const plotBottom = h - AXIS_H;
    const midY = (plotTop + plotBottom) / 2;
    const plotH = plotBottom - plotTop;

    const all = data.map((d) => ({
      data: d,
      tier: tierIndex(d.members),
      r: rScale(d.members),
      x: 0,
      y: 0,
    }));

    const byTier = d3.group(all, (n) => n.tier);

    // A force sim can't converge on thousands of dots in reasonable time, so
    // dense tiers get a golden-angle fill (even + organic, O(n)) and only the
    // sparse tiers — the ones with big, readable bubbles — are simulated.
    const DENSE = 500;
    const forceNodes = [];

    for (const [tier, group] of byTier) {
      const cx = colCenter.get(tier);
      const left = colLeft.get(tier);

      if (group.length <= DENSE) {
        for (const n of group) {
          n.x = cx + (Math.random() - 0.5) * colW * 0.5;
          n.y = midY + (Math.random() - 0.5) * plotH * 0.8;
        }
        forceNodes.push(...group);
        continue;
      }

      const maxR = d3.max(group, (n) => n.r) || 2;
      const spacing = maxR * 2 + 1.4;
      // Ellipse sized to hold the group at ~0.72 packing density.
      const area = (group.length * spacing * spacing) / 0.72;
      let b = Math.min(plotH / 2 - maxR, Math.sqrt((area * 2.6) / Math.PI) / 2);
      let a = Math.min(colW / 2 - maxR - 2, area / (Math.PI * b));
      b = Math.min(plotH / 2 - maxR, area / (Math.PI * a));

      const shuffled = d3.shuffle(group.slice());
      // Jitter scaled to the spacing breaks up the golden-angle spiral so the
      // cluster reads as an organic cloud instead of a lattice.
      const jitter = spacing * 0.5;
      shuffled.forEach((n, i) => {
        const t = Math.sqrt((i + 0.5) / shuffled.length);
        const theta = i * 2.399963229728653 + Math.random() * 0.6; // golden angle
        n.x = cx + a * t * Math.cos(theta) + (Math.random() - 0.5) * jitter;
        n.y = midY + b * t * Math.sin(theta) + (Math.random() - 0.5) * jitter;
        n.x = Math.max(left + n.r + 2, Math.min(left + colW - n.r - 2, n.x));
        n.y = Math.max(plotTop + n.r, Math.min(plotBottom - n.r, n.y));
      });
    }

    if (forceNodes.length) {
      const simulation = d3
        .forceSimulation(forceNodes)
        .force("x", d3.forceX((d) => colCenter.get(d.tier)).strength(0.85))
        .force("y", d3.forceY(midY).strength(0.02))
        .force(
          "collide",
          d3.forceCollide((d) => d.r + 1.1).strength(0.9)
        )
        .stop();

      // Clamping every tick keeps columns inside their lane, which forces the
      // cluster to grow vertically instead of bleeding across the separator.
      const clamp = (n) => {
        const left = colLeft.get(n.tier);
        n.x = Math.max(left + n.r + 2, Math.min(left + colW - n.r - 2, n.x));
        n.y = Math.max(plotTop + n.r, Math.min(plotBottom - n.r, n.y));
      };

      const ticks = forceNodes.length > 1500 ? 140 : 200;
      for (let i = 0; i < ticks; i++) {
        simulation.tick();
        for (const n of forceNodes) clamp(n);
      }
    }

    const sim = all;

    const columns = used.map((t, i) => ({
      label: t.label,
      count: t.count,
      x: colW * (i + 0.5),
      left: colW * i,
      width: colW,
    }));

    return { nodes: sim, columns };
  }, [servers, dims, colorScale]);

  useEffect(() => {
    if (!svgRef.current || !nodes.length) return;
    const svg = d3.select(svgRef.current);
    const zoom = d3
      .zoom()
      .scaleExtent([0.5, 12])
      .on("zoom", ({ transform }) => {
        requestAnimationFrame(() => setZoomT(transform));
        d3.select("#discord-tier-zoom").attr("transform", transform);
      });
    svg.call(zoom);
    return () => svg.on(".zoom", null);
  }, [nodes]);

  // One delegated hit test beats attaching handlers to thousands of nodes.
  const quadtree = useMemo(() => {
    if (!nodes.length) return null;
    return d3
      .quadtree()
      .x((d) => d.x)
      .y((d) => d.y)
      .addAll(nodes);
  }, [nodes]);

  const handleMove = (event) => {
    if (!quadtree || !svgRef.current) return;
    const rect = layoutRect(svgRef.current);
    const [mx, my] = zoomT.invert([layoutCoordinate(event.clientX) - rect.left, layoutCoordinate(event.clientY) - rect.top]);
    const found = quadtree.find(mx, my, 40);
    if (!found) {
      if (hovered) setHovered(null);
      return;
    }
    const dx = found.x - mx;
    const dy = found.y - my;
    const hit = dx * dx + dy * dy <= found.r * found.r ? found : null;
    if (hit !== hovered) setHovered(hit);
  };

  const showSkeleton = loading || dims.w <= 0 || (!nodes.length && servers?.length > 0);
  const isEmpty = !loading && !nodes.length && !(servers?.length > 0);
  const k = zoomT?.k || 1;

  // Only the handful of bubbles big enough to read get a label.
  const labelled = useMemo(
    () => nodes.filter((d) => d.r * k >= 13).slice(0, 80),
    [nodes, k]
  );

  return (
    <div className="discord-market discord-tier" ref={containerRef}>
      {isEmpty ? (
        <div className="discord-empty">No visible Discord servers match this filter.</div>
      ) : showSkeleton ? (
        <div className="discord-market-skeleton">
          <SkeletonMarketViewBubbleChart
            width={Math.max(480, Math.min(Math.min(dims.w || 1200, dims.h || 720), 900))}
            height={Math.max(480, Math.min(Math.min(dims.w || 1200, dims.h || 720), 900))}
            bubbleCount={200}
          />
        </div>
      ) : (
        <>
          <svg
            ref={svgRef}
            width={dims.w}
            height={dims.h}
            style={{ display: "block", cursor: "grab" }}
            onMouseMove={handleMove}
            onMouseLeave={() => setHovered(null)}
          >
            <g id="discord-tier-zoom">
              {columns.map((c, i) =>
                i === 0 ? null : (
                  <line
                    key={`sep:${c.label}`}
                    className="discord-tier-sep"
                    x1={c.left}
                    x2={c.left}
                    y1={TOP_PAD}
                    y2={dims.h - AXIS_H}
                  />
                )
              )}

              <g className="discord-tier-dots">
                {nodes.map((d, i) => (
                  <circle
                    key={d.data.server_id || i}
                    cx={d.x}
                    cy={d.y}
                    r={d.r}
                    fill={d.data.color}
                    opacity={0.85}
                  />
                ))}
              </g>

              {hovered && (
                <circle
                  cx={hovered.x}
                  cy={hovered.y}
                  r={hovered.r * 1.18}
                  fill={hovered.data.color}
                  stroke="rgba(255,255,255,0.85)"
                  strokeWidth={Math.max(1, 1.5 / Math.pow(k, 0.85))}
                />
              )}

              {labelled.map((d, i) => {
                const visibleR = d.r * k;
                const maxChars = visibleR > 80 ? 28 : visibleR > 44 ? 22 : 12;
                const name = d.data.name;
                return (
                  <text
                    key={`lbl:${d.data.server_id || i}`}
                    x={d.x}
                    y={d.y}
                    textAnchor="middle"
                    dy="0.35em"
                    fontSize={10 / Math.pow(k, 0.8)}
                    fill="#fff"
                    stroke={`rgba(0,0,0,${Math.max(0.1, 0.5 / Math.pow(k, 0.85))})`}
                    strokeWidth={Math.max(0.25, 1 / Math.pow(k, 0.9))}
                    style={{
                      pointerEvents: "none",
                      fontWeight: 500,
                      letterSpacing: "-0.2px",
                      paintOrder: "stroke",
                      fontFamily: BUBBLE_LABEL_FONT,
                    }}
                  >
                    {name.length > maxChars ? name.slice(0, maxChars) + "…" : name}
                  </text>
                );
              })}
            </g>

            {/* Axis stays fixed while the plot pans/zooms. */}
            <g className="discord-tier-axis" transform={`translate(0,${dims.h - AXIS_H})`}>
              <line x1={0} x2={dims.w} y1={0} y2={0} className="discord-tier-axis-line" />
              {columns.map((c) => (
                <g key={c.label} transform={`translate(${c.x},0)`}>
                  <text className="discord-tier-axis-label" y={20} textAnchor="middle">
                    {c.label}
                  </text>
                  <text className="discord-tier-axis-count" y={35} textAnchor="middle">
                    {formatCompactNumber(c.count)} server{c.count === 1 ? "" : "s"}
                  </text>
                </g>
              ))}
            </g>
          </svg>

          {hovered && (
            <DiscordBubbleTooltip
              datum={hovered.data}
              x={zoomT?.applyX?.(hovered.x) ?? hovered.x}
              y={zoomT?.applyY?.(hovered.y) ?? hovered.y}
              r={(hovered.r || 20) * k}
              width={dims.w}
              height={dims.h}
            />
          )}
        </>
      )}
    </div>
  );
}
